import FitCurveIcon from '@/assets/images/FitCurveIcon.svg';
import FitPrimeIcon from '@/assets/images/FitPrimeIcon.svg';
import FitVarsityIcon from '@/assets/images/FitVarsityIcon.svg';
import Info from '@/assets/images/Info.svg';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Modal } from '@/components/ui/modal';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { GET_ALL_PROGRAM } from '@/Graphql/queries';
import { authState } from '@/vars/auth';
import { useLazyQuery, useMutation, useQuery, useReactiveVar } from '@apollo/client';
import { Elements } from '@stripe/react-stripe-js';
import { Appearance, loadStripe } from '@stripe/stripe-js';
import { CheckCircle, LoaderCircle, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { CARD_SETUP_INTENT, PRODUCT_PAYMENT_INTENT } from '../GraphQL/mutation';
import { GET_ALL_PLANS, GET_MY_SAVED_CARD, VALIDATE_PROMOTION_CODE } from '../GraphQL/queries';
import CheckoutForm from './CheckoutForm';

interface DiscountInfo {
  promotionCode: string;
  percentOff: number;
  originalAmount: number;
  discountAmount: number;
  finalAmount: number;
}
interface BuySubsctriptionProps {
  open: boolean;
  defaultPlan?: string;
  selectedProgram?: string;
  handleClose: () => void;
}

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_KEY);

const BuySubsctription: React.FC<BuySubsctriptionProps> = ({
  open,
  defaultPlan,
  selectedProgram: initialProgram,
  handleClose,
}) => {
  const [paymentStatus, setPaymentStatus] = useState('');
  const [selectedPlan, setSelectedPlan] = useState(defaultPlan || '');
  const [selectedProgram, setSelectedProgram] = useState<string>(initialProgram || 'fitvarsity');
  const [selectedProgramId, setSelectedProgramId] = useState<string>('');
  const { userDetails } = useReactiveVar(authState);

  // Promo code state
  const [promoCode, setPromoCode] = useState('');
  const [promoCodeError, setPromoCodeError] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState<DiscountInfo | null>(null);
  const [isPromoApplied, setIsPromoApplied] = useState(false);

  const { data: programsData, loading: programsLoading } = useQuery(GET_ALL_PROGRAM, {
    variables: {
      limit: 10,
      offset: 0,
    },
  });

  const { data: plans, loading: planLoading } = useQuery(GET_ALL_PLANS, {
    variables: {
      sort: { sessionCount: 1 },
      filter: {
        ...(selectedProgramId && { programId: selectedProgramId }),
        status: true,
      },
    },
    skip: !selectedProgramId, // Skip query if no program is selected
    onCompleted: (res) => {
      if (selectedPlan) {
        res?.getAllPlan.find((item) => item?._id === selectedPlan)
          ? ''
          : setSelectedPlan(res?.getAllPlan[0]?._id || '');
      } else {
        setSelectedPlan(res?.getAllPlan[0]?._id || '');
      }
    },
  });
  useQuery(GET_MY_SAVED_CARD);
  const [fetchCardSetup] = useMutation(CARD_SETUP_INTENT);
  const [fetchPayemntIntent, { data: paymentIntent, loading: paymentIntentLoading }] =
    useMutation(PRODUCT_PAYMENT_INTENT);

  // Promo code validation query
  const [validatePromoCode, { loading: isValidatingPromo }] = useLazyQuery(VALIDATE_PROMOTION_CODE, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (data?.validatePromotionCode?.valid) {
        setAppliedDiscount(data.validatePromotionCode.discount as DiscountInfo);
        setIsPromoApplied(true);
        setPromoCodeError('');
      } else {
        setPromoCodeError(data?.validatePromotionCode?.message || 'Invalid promo code');
        setAppliedDiscount(null);
        setIsPromoApplied(false);
      }
    },
    onError: (error) => {
      setPromoCodeError(error.message || 'Failed to validate promo code');
      setAppliedDiscount(null);
      setIsPromoApplied(false);
    },
  });

  useEffect(() => {
    fetchCardSetup();
  }, []);

  useEffect(() => {
    if (initialProgram) {
      setSelectedProgram(initialProgram);
    }
  }, [initialProgram]);

  // Initialize program ID when programs data loads or when selectedProgram changes
  useEffect(() => {
    if (programsData?.getAllProgram && selectedProgram) {
      const program = programsData.getAllProgram.find(
        (p) => p?.programName?.toLowerCase() === selectedProgram.toLowerCase(),
      );
      if (program) {
        setSelectedProgramId(program._id);
      }
    }
  }, [programsData, selectedProgram]);

  useEffect(() => {
    if (selectedPlan && userDetails?._id) {
      fetchPayemntIntent({
        variables: {
          data: {
            planId: selectedPlan,
            userId: userDetails?._id,
            ...(isPromoApplied && promoCode && {
              promotionCode: promoCode.trim().substring(0, 5),
            }),
          },
        },
      });
    }
  }, [selectedPlan, userDetails, isPromoApplied, appliedDiscount]);

  const appearance: Appearance = {
    theme: 'night',
    variables: {
      colorPrimary: '#0570de',
      colorBackground: '#151525',
      borderRadius: '12px',
    },
  };

  const loader = 'auto';

  // Generate programs from API data
  const programs =
    programsData?.getAllProgram?.map((program) => {
      const programName = program?.programName?.toLowerCase() || '';

      // Map program names to icons
      const getIcon = (name: string) => {
        switch (name) {
          case 'fitvarsity':
            return FitVarsityIcon;
          case 'fitcurve':
            return FitCurveIcon;
          case 'fitprime':
            return FitPrimeIcon;
          default:
            return FitVarsityIcon; // fallback
        }
      };

      return {
        id: programName,
        _id: program?._id || '',
        name: program?.programName || '',
        description: program?.description || '',
        icon: getIcon(programName),
      };
    }) || [];

  // Handler for program selection
  const handleProgramSelect = (program: any) => {
    setSelectedProgram(program.id);
    setSelectedProgramId(program._id);
  };

  // Reset promo code when plan changes
  const handlePlanSelect = (planId: string) => {
    setSelectedPlan(planId);
    // Reset promo code state when plan changes
    setPromoCode('');
    setPromoCodeError('');
    setAppliedDiscount(null);
    setIsPromoApplied(false);
  };

  // Handle apply promo code
  const handleApplyPromoCode = () => {
    if (!promoCode.trim()) {
      setPromoCodeError('Please enter a promo code');
      return;
    }
    validatePromoCode({
      variables: {
        code: promoCode.trim().substring(0, 5),
        planId: selectedPlan,
      },
    });
  };

  // Handle remove promo code
  const handleRemovePromoCode = () => {
    setPromoCode('');
    setPromoCodeError('');
    setAppliedDiscount(null);
    setIsPromoApplied(false);
  };

  // Get current selected plan details
  const currentPlan = plans?.getAllPlan.find((item) => item?._id === selectedPlan);

  return (
    <Modal
      title='Buy Subscriptions'
      open={open}
      onClose={handleClose}
      className='bg-[#151525] w-[95vw] sm:w-[90vw] lg:w-auto'
    >
      <div className='relative max-h-[calc(100vh-150px)] overflow-y-auto'>
        {paymentStatus === 'completed' ? (
          ''
        ) : (
          <div className='px-4 sm:px-6 lg:px-7'>
            {/* Select Program Section */}
            <div className='my-4 flex flex-col gap-3 rounded-[20px] bg-[#00001C] p-4 sm:my-5 sm:p-[18px]'>
              <p className='text-white font-medium text-base'>Select Program</p>
              <div className='grid grid-cols-1 gap-2.5 md:grid-cols-2 lg:grid-cols-3'>
                {programsLoading
                  ? Array(3)
                    .fill(0)
                    .map((_, index) => (
                      <Skeleton key={index} className='h-[120px] bg-bgSecondary' />
                    ))
                  : programs.map((program) => (
                    <div
                      key={program.id}
                      className={`flex flex-col gap-3 rounded-xl p-[14px_12px] transition-all sm:p-[18px_14px] ${selectedProgram === program.id
                        ? 'border-2 border-[#0A7AFF] bg-[#181A32]'
                        : 'bg-gradient-to-br from-[#2F2F2F] to-[#2F2F2F]'
                        }`}
                      onClick={() => handleProgramSelect(program)}
                    >
                      <div className='flex items-center gap-2.5 sm:gap-3'>
                        <div className='flex h-7 w-7 items-center justify-center sm:h-8 sm:w-8'>
                          <img src={program.icon} alt={program.name} className='h-7 w-7 sm:h-8 sm:w-8' />
                        </div>
                        <span className='text-base font-medium leading-[22px] tracking-[0.02em] text-white sm:text-xl sm:leading-[24px]'>
                          {program.name}
                        </span>
                      </div>
                      <p className='text-[#C5C8D0] text-xs leading-[17px]'>
                        {program.description}
                      </p>
                    </div>
                  ))}
              </div>
            </div>

            {/* Select Package Section */}
            <div className='mb-5 flex flex-col gap-3 rounded-[20px] bg-[#00001C] p-4 sm:p-[18px]'>
              <div className='flex flex-col gap-2'>
                <p className='text-white font-medium text-base'>Select Package</p>
                <div className='flex gap-1.5'>
                  <div className='flex items-center gap-2.5 pt-[1px]'>
                    <img src={Info} alt='info' className='w-[18px] h-[18px]' />
                  </div>
                  <p className='text-[#E9CC7C] text-sm leading-[20px]'>
                    Sessions don&apos;t expire. Follow suggested pace to stay on track.
                  </p>
                </div>
              </div>
              <div className='grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3'>
                {plans?.getAllPlan.map((plan) => {
                  const isSelected = selectedPlan === plan?._id;

                  return (
                    <div
                      key={plan?._id ?? ''}
                      className={`flex items-center justify-between gap-3 rounded-xl p-[14px_12px] transition-all sm:p-[18px_14px] ${isSelected
                        ? 'border-2 border-[#0A7AFF] bg-[#181A32]'
                        : 'bg-gradient-to-br from-[#2F2F2F] to-[#2F2F2F]'
                        }`}
                      onClick={() => handlePlanSelect(plan?._id || '')}
                    >
                      <div className='flex flex-col gap-1.5'>
                        <span className='text-white font-semibold text-base leading-[19px]'>
                          {plan?.sessionCount} Sessions
                        </span>
                        <span className='text-[#C5C8D0] font-medium text-xs leading-[15px]'>
                          Recommended: {plan?.recommendedNoOfDays} Days
                        </span>
                      </div>
                      <div className='flex flex-col items-end gap-0.5'>
                        {plan?.originalPrice !== 0 && (
                          <span className='text-[#E9CC7C] text-xs line-through'>
                            ${plan?.originalPrice}
                          </span>
                        )}
                        <span className='text-[#E9CC7C] font-bold text-base leading-[22px] tracking-[0.02em]'>
                          ${plan?.price}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Promo Code Section - Only show if plan has promoCodeEnabled */}
            {currentPlan?.promoCodeEnabled && (
              <div className='mb-5 flex flex-col gap-3 rounded-[20px] bg-[#00001C] p-4 sm:p-[18px]'>
                <p className='text-white font-medium text-base'>Have a discount code?</p>

                {isPromoApplied && appliedDiscount ? (
                  // Show applied promo code with discount breakdown
                  <div className='flex flex-col gap-3'>
                    <div className='flex items-center justify-between bg-[#1A3D2E] p-3 rounded-xl'>
                      <div className='flex items-center gap-2'>
                        <CheckCircle className='w-5 h-5 text-[#4ADE80]' />
                        <span className='text-[#4ADE80] font-medium'>
                          {promoCode} applied ({appliedDiscount.percentOff}% off)
                        </span>
                      </div>
                      <button
                        onClick={handleRemovePromoCode}
                        className='text-[#C5C8D0] hover:text-white transition-colors'
                      >
                        <X className='w-5 h-5' />
                      </button>
                    </div>

                    {/* Discount Breakdown */}
                    <div className='flex flex-col gap-2 bg-[#181A32] p-3 rounded-xl'>
                      <div className='flex justify-between text-sm'>
                        <span className='text-[#C5C8D0]'>Original Price</span>
                        <span className='text-white'>${appliedDiscount.originalAmount.toFixed(2)}</span>
                      </div>
                      <div className='flex justify-between text-sm'>
                        <span className='text-[#4ADE80]'>Discount ({appliedDiscount.percentOff}% off)</span>
                        <span className='text-[#4ADE80]'>-${appliedDiscount.discountAmount.toFixed(2)}</span>
                      </div>
                      <Separator className='bg-[#FFFFFF21]' />
                      <div className='flex justify-between'>
                        <span className='text-white font-medium'>Final Price</span>
                        <span className='text-[#E9CC7C] font-bold'>${appliedDiscount.finalAmount.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  // Show promo code input
                  <div className='flex flex-col gap-2'>
                    <div className='flex gap-2'>
                      <Input
                        id='promo-code-input'
                        type='text'
                        placeholder='Enter promo code'
                        value={promoCode}
                        onChange={(e) => {
                          setPromoCode(e.target.value);
                          if (promoCodeError) setPromoCodeError('');
                        }}
                        className='flex-1 bg-[#181A32] border-[#2F2F2F] text-white placeholder:text-[#6B7280]'
                      />
                      <Button
                        id='apply-promo-btn'
                        onClick={handleApplyPromoCode}
                        disabled={isValidatingPromo || !promoCode.trim()}
                        variant='secondary'
                        className='px-6'
                      >
                        {isValidatingPromo ? (
                          <LoaderCircle className='h-4 w-4 animate-spin' />
                        ) : (
                          'Apply'
                        )}
                      </Button>
                    </div>
                    {promoCodeError && (
                      <p className='text-[#DC1D00] text-sm'>{promoCodeError}</p>
                    )}
                  </div>
                )}
              </div>
            )}

            <Separator className='bg-[#FFFFFF21] w-full' />
          </div>
        )}
        {planLoading || paymentIntentLoading ? (
          <PaymentLoading />
        ) : paymentIntent?.productPaymentIntent?.paymentIntentSecret ? (
          <Elements
            options={{
              clientSecret: paymentIntent?.productPaymentIntent?.paymentIntentSecret,
              appearance,
              loader,
              customerSessionClientSecret:
                paymentIntent?.productPaymentIntent?.customerSessionSecret,
            }}
            stripe={stripePromise}
          >
            {/* {paymentStatus === 'completed' ? (
              <CompletePage
                clientSecret={paymentIntent?.productPaymentIntent?.paymentIntentSecret}
                handleClose={handleClose}
              />
            ) : ( */}
            <CheckoutForm
              handleClose={handleClose}
              setPaymentStatus={setPaymentStatus}
              selectedPlan={selectedPlan}
              setSelectedPlan={setSelectedPlan}
              plans={plans}
              price={plans?.getAllPlan.find((item) => item?._id === selectedPlan)?.price || 0}
              discount={appliedDiscount}
            />
            {/* )} */}
          </Elements>
        ) : (
          ''
        )}
      </div>
    </Modal>
  );
};

const PaymentLoading = () => (
  <div className='p-4 sm:p-6 lg:p-7'>
    <div className='w-full rounded-lg bg-transparent p-4 shadow-lg sm:p-6'>
      <Skeleton className='h-8 w-3/4 mb-4 bg-bgSecondary' />
      <Skeleton className='h-6 w-1/2 mb-4 bg-bgSecondary' />
      <Skeleton className='h-4 w-full mb-2 bg-bgSecondary' />
      <Skeleton className='h-4 w-full mb-2 bg-bgSecondary' />
      <Skeleton className='h-4 w-full mb-2 bg-bgSecondary' />
      <Skeleton className='h-4 w-full mb-2 bg-bgSecondary' />
      <Skeleton className='h-12 w-full mb-4 bg-bgSecondary' />
      <Skeleton className='h-10 w-full bg-bgSecondary' />
    </div>
  </div>
);

export default BuySubsctription;
